import { eq, and, isNull, sql, asc, lte, gt, gte, inArray } from 'drizzle-orm';
import { addDays, format, parseISO, subDays } from 'date-fns';
import { monthCycleContaining } from '@/lib/monthCycle';
import { getMonthStartDay } from '@/lib/monthStart';
import { db } from '@/db/client';
import { accounts, transactions, reservas, reservaMovements, recurringTransactions, creditCards } from '@/db/schema';
import { generateId } from '@/lib/id';
import { roundMonetaryValue } from '@/lib/formatting';
import { buildIncludedAccountBalanceMap } from '@/lib/accountTotals';
import { buildBalanceTimeline, totalBalanceAfterImpacts, type BalanceBoundary, type DatedBalanceImpactRow } from '@/lib/balanceTimeline';
import { buildProjectedRecurringTransactions } from '@/lib/projectedRecurringTransactions';
import { compareIso } from '@/lib/compareIso';
import { HandledByCallerError } from '@/lib/errorUtils';

export type Account = typeof accounts.$inferSelect;
export type NewAccount = Omit<typeof accounts.$inferInsert, 'id' | 'createdAt' | 'updatedAt'>;

function buildAccountConditions({
    includeArchived = false,
    includeExcludedFromTotals = true,
}: {
    includeArchived?: boolean;
    includeExcludedFromTotals?: boolean;
} = {}) {
    const conditions = [isNull(accounts.deletedAt)];

    if (!includeArchived) {
        conditions.push(eq(accounts.isArchived, 0));
    }

    if (!includeExcludedFromTotals) {
        conditions.push(eq(accounts.isExcludedFromTotals, 0));
    }

    return conditions;
}

export async function getAccounts(includeArchived = false): Promise<Account[]> {
    return db
        .select()
        .from(accounts)
        .where(and(...buildAccountConditions({ includeArchived })))
        .orderBy(asc(accounts.sortOrder), asc(accounts.name));
}

export async function getIncludedAccounts(includeArchived = false): Promise<Account[]> {
    return db
        .select()
        .from(accounts)
        .where(and(...buildAccountConditions({ includeArchived, includeExcludedFromTotals: false })))
        .orderBy(asc(accounts.sortOrder), asc(accounts.name));
}

export async function getAccountById(id: string): Promise<Account | null> {
    const results = await db
        .select()
        .from(accounts)
        .where(and(eq(accounts.id, id), isNull(accounts.deletedAt)));
    // null (não undefined): é a queryFn de ['accounts','detail',id] e o React Query proíbe
    // undefined — excluir a conta com a tela de detalhe aberta estouraria no refetch.
    return results[0] ?? null;
}

export async function createAccount(data: NewAccount): Promise<Account> {
    const id = generateId();
    const now = new Date().toISOString();
    await db.insert(accounts).values({
        ...data,
        id,
        currentBalance: data.initialBalance ?? 0,
        createdAt: now,
        updatedAt: now,
    });
    return (await getAccountById(id))!;
}

export async function updateAccount(id: string, data: Partial<NewAccount>): Promise<void> {
    await db
        .update(accounts)
        .set({ ...data, updatedAt: new Date().toISOString() })
        .where(eq(accounts.id, id));

    // Editing the initial balance shifts the running balance too — keep the
    // invariant currentBalance = initialBalance + Σ(confirmed tx) intact, or it
    // silently breaks until the next recalc (e.g. a balance adjustment).
    if (data.initialBalance !== undefined) {
        await recalculateAccountBalance(id);
    }
}

/**
 * Arquivar conta com caixinha ATIVA é recusado (ADR 0192): a caixinha não pode ficar parada
 * numa conta escondida. Quem chama (o `requestArchive` da UI) já barra antes e oferece o
 * caminho — resgatar ou arquivar a caixinha —; o erro aqui é a trava de quem chegar por outro
 * caminho. `HandledByCallerError`: quem chama abre o diálogo de bloqueio, sem o toast global.
 */
export class AccountHasActiveReservasError extends HandledByCallerError {
    constructor(readonly count: number) {
        super('A conta tem caixinha ativa. Resgate ou arquive a caixinha antes de arquivar a conta.');
        this.name = 'AccountHasActiveReservasError';
    }
}

/** Caixinhas ATIVAS da conta: não arquivadas e não excluídas, mesmo com R$ 0 (ADR 0192). */
export async function countActiveReservasByAccount(accountId: string): Promise<number> {
    const [row] = await db
        .select({ n: sql<number>`count(*)` })
        .from(reservas)
        .where(and(eq(reservas.accountId, accountId), eq(reservas.isArchived, 0), isNull(reservas.deletedAt)));
    return Number(row?.n ?? 0);
}

export async function archiveAccount(id: string): Promise<void> {
    const activeReservas = await countActiveReservasByAccount(id);
    if (activeReservas > 0) throw new AccountHasActiveReservasError(activeReservas);
    await db
        .update(accounts)
        .set({ isArchived: 1, updatedAt: new Date().toISOString() })
        .where(eq(accounts.id, id));
}

export async function unarchiveAccount(id: string): Promise<void> {
    await db
        .update(accounts)
        .set({ isArchived: 0, updatedAt: new Date().toISOString() })
        .where(eq(accounts.id, id));
}

export async function deleteAccount(id: string): Promise<void> {
    const now = new Date().toISOString();
    await db
        .update(accounts)
        .set({ deletedAt: now })
        .where(eq(accounts.id, id));

    // Desvincula os cartões que pagavam por esta conta. Sem isto o cartão fica com
    // `payment_account_id` apontando pra conta apagada: enquanto a linha ainda existe
    // (soft-delete) a fatura pendente ia parar numa conta morta, e assim que ela some
    // de fato — "Limpar excluídos" ou um round-trip de backup, ambos escrevem com FK
    // OFF — a referência vira órfã e toda escrita da fatura estoura FOREIGN KEY
    // (Sentry REACT-NATIVE-7). Cartão sem conta de pagamento é estado suportado: a UI
    // mostra o campo vazio e o usuário reescolhe.
    await db
        .update(creditCards)
        .set({ paymentAccountId: null, updatedAt: now })
        .where(eq(creditCards.paymentAccountId, id));
}

export async function recalculateAccountBalance(accountId: string): Promise<void> {
    const account = await getAccountById(accountId);
    if (!account) return;

    const balance = await balanceFromEntries(accountId, account.initialBalance);
    await db
        .update(accounts)
        .set({ currentBalance: balance, updatedAt: new Date().toISOString() })
        .where(eq(accounts.id, accountId));
}

/**
 * Sync (ADR 0163): refaz o saldo das contas a partir dos lançamentos que chegaram de outro
 * aparelho. O saldo guardado na linha é uma célula só, e o last-write-wins do sync fica com o
 * valor de UM aparelho quando os dois lançaram na mesma conta antes de se verem.
 *
 * Grava só onde diverge: reescrever o mesmo valor não emite nada na captura, e é isso que
 * impede o eco entre os aparelhos. Devolve quantas contas corrigiu.
 */
export async function reconcileAccountBalances(accountIds: Iterable<string>): Promise<number> {
    let fixed = 0;
    for (const id of new Set(accountIds)) {
        const [account] = await db
            .select({ initialBalance: accounts.initialBalance, currentBalance: accounts.currentBalance })
            .from(accounts)
            .where(eq(accounts.id, id));
        if (!account) continue;

        const balance = await balanceFromEntries(id, account.initialBalance);
        if (Math.round(balance * 100) === Math.round(account.currentBalance * 100)) continue;
        await db.update(accounts).set({ currentBalance: balance }).where(eq(accounts.id, id));
        fixed += 1;
    }
    return fixed;
}

/** Saldo inicial + lançamentos efetivados e vivos da conta. */
async function balanceFromEntries(accountId: string, initialBalance: number): Promise<number> {
    const result = await db
        .select({
            total: sql<number>`COALESCE(SUM(
                CASE
                    WHEN ${transactions.type} = 'income' THEN ${transactions.amount}
                    WHEN ${transactions.type} = 'expense' THEN -${transactions.amount}
                    WHEN ${transactions.type} = 'transfer' AND ${transactions.accountId} = ${accountId} THEN -${transactions.amount}
                    WHEN ${transactions.type} = 'transfer' AND ${transactions.transferDestinationAccountId} = ${accountId} THEN ${transactions.amount}
                    WHEN ${transactions.type} = 'adjustment' THEN ${transactions.amount}
                    ELSE 0
                END
            ), 0)`.as('total'),
        })
        .from(transactions)
        .where(
            and(
                sql`(${transactions.accountId} = ${accountId} OR ${transactions.transferDestinationAccountId} = ${accountId})`,
                isNull(transactions.deletedAt),
                eq(transactions.isConfirmed, 1),
            ),
        );

    // Em centavos: a soma em float deixava resíduo (1e-13), e o "saldo − reservado" de quem
    // guardou tudo virava −0,0000…1 — "R$ 0,00" em vermelho com o aviso de caixinha submersa.
    return roundMonetaryValue(initialBalance + (result[0]?.total ?? 0));
}

export async function getTotalBalance(): Promise<number> {
    const result = await db
        .select({
            total: sql<number>`COALESCE(SUM(${accounts.currentBalance}), 0)`.as('total'),
        })
        .from(accounts)
        .where(and(...buildAccountConditions({ includeExcludedFromTotals: false })));
    return result[0]?.total ?? 0;
}

export interface AvailableBalance {
    /** Σ saldo das contas incluídas no total (patrimônio líquido em conta). */
    total: number;
    /** Σ reservado em caixinhas ATIVAS das mesmas contas incluídas. */
    reserved: number;
    /** `total − reserved`, SEM limitar em zero (igual ao topo da Home — R02-11). */
    available: number;
}

/**
 * Disponível agregado = saldo total das contas incluídas − reservado em caixinhas das contas
 * incluídas. É o número de destaque da Home (ADR 0020) e, desde 15/09/2026, o ponto de partida
 * das Projeções de curto e médio prazo (e do What-if, que parte do médio prazo).
 *
 * Só as caixinhas de contas INCLUÍDAS entram no reservado — o mesmo recorte de `getTotalBalance`:
 * se a conta está fora do total, nem o saldo nem o reservado dela contam. Caixinha arquivada ou
 * excluída também fica de fora (ela já não abate o Disponível da conta — ver archiveReserva).
 *
 * NÃO limita em zero: reservado acima do saldo deixa o Disponível negativo, e é isso que a Home
 * mostra. Quem precisa do valor clampado por conta usa `getAvailableByAccount` (reservaRepository).
 */
export async function getAvailableBalance(): Promise<AvailableBalance> {
    const [totalRow, reservedRow] = await Promise.all([
        db
            .select({
                total: sql<number>`COALESCE(SUM(${accounts.currentBalance}), 0)`.as('total'),
            })
            .from(accounts)
            .where(and(...buildAccountConditions({ includeExcludedFromTotals: false }))),
        db
            .select({
                total: sql<number>`COALESCE(SUM(${reservas.currentAmount}), 0)`.as('total'),
            })
            .from(reservas)
            .innerJoin(accounts, eq(reservas.accountId, accounts.id))
            .where(
                and(
                    eq(reservas.isArchived, 0),
                    isNull(reservas.deletedAt),
                    ...buildAccountConditions({ includeExcludedFromTotals: false }),
                ),
            ),
    ]);
    const total = roundMonetaryValue(totalRow[0]?.total ?? 0);
    const reserved = roundMonetaryValue(reservedRow[0]?.total ?? 0);
    return {
        total,
        reserved,
        available: Math.round((total - reserved) * 100) / 100,
    };
}

/**
 * Variação do **Disponível** (saldo total − reservado das contas incluídas) NO mês corrente,
 * comparada ao fim do mês passado — o "tanto a mais/menos" do chip, no espírito do comparativo
 * do cartão. Mede só o que JÁ aconteceu: transações **confirmadas com data <= hoje** (nada de
 * futuro/projetado) e movimentos de caixinha do mês.
 *
 *   Δ Disponível = Δ saldo total − Δ reservado
 *
 * Δ saldo total = saldo(hoje) − saldo(fim do mês passado), ambos reconstruídos de initialBalance
 * + impactos confirmados (imune a currentBalance defasado). Δ reservado = líquido de
 * guardar/resgatar/rendimento (deposit/yield − withdraw) das caixinhas neste mês: guardar reduz o
 * Disponível, resgatar aumenta — por isso entra com sinal trocado.
 *
 * LIMITE CONHECIDO: arquivar/excluir/reativar uma caixinha move o reservado SEM criar movimento
 * (só entra/sai do somatório — ver archiveReserva/deleteReserva). Então, num mês em que o usuário
 * fizer isso, o número pode ficar levemente defasado; guardar/resgatar/rendimento (o caso comum) é
 * exato. Reconstruir o reservado numa data passada com precisão não é possível no modelo atual
 * (arquivamento não é datado).
 */
export async function getCurrentMonthBalanceDelta(monthStartDay: number = getMonthStartDay()): Promise<number> {
    const now = new Date();
    const today = format(now, 'yyyy-MM-dd');
    // Véspera do PERÍODO corrente (ADR 0190): o fim do mês passado no calendário; o dia 19 com
    // Dia de início 20.
    const endOfPreviousMonth = format(subDays(parseISO(monthCycleContaining(today, monthStartDay).startDate), 1), 'yyyy-MM-dd');

    const includedAccounts = await getIncludedAccounts();
    const includedIds = new Set(includedAccounts.map((account) => account.id));

    // Impactos confirmados até HOJE (sem futuro). Uma query; particiona no JS pros dois cortes.
    const movementRows = await db
        .select({
            date: transactions.date,
            type: transactions.type,
            amount: transactions.amount,
            accountId: transactions.accountId,
            transferDestinationAccountId: transactions.transferDestinationAccountId,
        })
        .from(transactions)
        .where(
            and(
                isNull(transactions.deletedAt),
                eq(transactions.isConfirmed, 1),
                lte(transactions.date, today),
            ),
        );

    const startRows = movementRows.filter((row) => row.date <= endOfPreviousMonth);
    const totalAtMonthStart = totalBalanceAfterImpacts(
        buildIncludedAccountBalanceMap(includedAccounts, 'initialBalance'),
        startRows,
    );
    const totalToday = totalBalanceAfterImpacts(
        buildIncludedAccountBalanceMap(includedAccounts, 'initialBalance'),
        movementRows,
    );
    const deltaTotal = totalToday - totalAtMonthStart;

    // Líquido de reservado no mês (deposit/yield +, withdraw −); só caixinhas de contas incluídas.
    const reservaRows = await db
        .select({
            type: reservaMovements.type,
            amount: reservaMovements.amount,
            accountId: reservas.accountId,
        })
        .from(reservaMovements)
        .innerJoin(reservas, eq(reservaMovements.reservaId, reservas.id))
        .where(
            and(
                isNull(reservas.deletedAt),
                gt(reservaMovements.date, endOfPreviousMonth),
                lte(reservaMovements.date, today),
            ),
        );

    let deltaReserved = 0;
    for (const row of reservaRows) {
        if (!includedIds.has(row.accountId)) continue;
        deltaReserved += row.type === 'withdraw' ? -row.amount : row.amount;
    }

    return Math.round((deltaTotal - deltaReserved) * 100) / 100;
}

/**
 * Saldo acumulado por dia do extrato (preferência "Saldo acumulado por dia"): o
 * **Disponível** das contas incluídas ao FIM de cada dia com movimento confirmado no
 * período — saldo total (initialBalance + impactos confirmados) MENOS o reservado nas
 * caixinhas ativas, espelhando o "saldo disponível" que o app mostra em todo lugar
 * (topo da Home, contas, seletores). É posição, não fluxo — imune a currentBalance
 * defasado e ao modo de exibição da lista (fatura agrupada, compras separadas etc.).
 * Pendentes/projetadas não movem o acumulado.
 *
 * `includeOpening` (sub-preferência): true = o dia 1º herda o saldo anterior (saldo
 * inicial das contas + tudo confirmado antes de `startDate`) — semântica de extrato
 * bancário; false = parte de R$ 0 no início do período ("quanto o mês rendeu").
 *
 * O reservado é reconstruído por dia dos MOVIMENTOS datados das caixinhas ativas
 * (deposit/yield somam, withdraw subtrai — a mesma conta do `currentAmount`), com o
 * mesmo tratamento de abertura do saldo. Assim o último dia bate exato com o Disponível
 * da Home, e uma caixinha criada há pouco não rebaixa retroativamente os dias passados
 * (o depósito dela é datado). LIMITE: arquivar/excluir uma caixinha solta o reservado
 * sem movimento datado — dias passados podem superestimar levemente o Disponível nesse
 * caso raro (mesma limitação conhecida de getCurrentMonthBalanceDelta). Pode ficar
 * NEGATIVO (caixinha submersa) — mostra o negativo honesto, não zero.
 *
 * `boundaryScope`: 'movement' (padrão) emite só dias com movimento confirmado — a UI não
 * mostra saldo nos demais (mesma regra do subtotal do dia). 'all' emite TODO dia do
 * período: dia parado carrega o saldo do anterior — é o que o extrato quer quando o dia
 * tem só pendência (o "Saldo do dia" efetivado existe mesmo sem nada ter caído nele).
 */
export async function getDailyBalanceTimeline(params: {
    startDate: string;
    endDate: string;
    includeOpening: boolean;
    boundaryScope?: 'movement' | 'all';
}): Promise<Record<string, number>> {
    const includedAccounts = await getIncludedAccounts();
    if (includedAccounts.length === 0) return {};
    const includedIds = includedAccounts.map((account) => account.id);

    const seed = params.includeOpening
        ? buildIncludedAccountBalanceMap(includedAccounts, 'initialBalance')
        : new Map(includedAccounts.map((account) => [account.id, 0]));

    // Com saldo anterior, os impactos pré-período entram no walk (o cursor do
    // buildBalanceTimeline aplica tudo <= 1ª fronteira antes de fotografar).
    const movementRows = await db
        .select({
            date: transactions.date,
            type: transactions.type,
            amount: transactions.amount,
            accountId: transactions.accountId,
            transferDestinationAccountId: transactions.transferDestinationAccountId,
        })
        .from(transactions)
        .where(
            and(
                isNull(transactions.deletedAt),
                eq(transactions.isConfirmed, 1),
                lte(transactions.date, params.endDate),
                sql`${transactions.type} IN ('income', 'expense', 'transfer', 'adjustment')`,
                ...(params.includeOpening ? [] : [gte(transactions.date, params.startDate)]),
            ),
        )
        .orderBy(asc(transactions.date), asc(transactions.createdAt));

    const boundaries: BalanceBoundary[] = [];
    if (params.boundaryScope === 'all') {
        boundaries.push(...eachDateBetween(params.startDate, params.endDate)
            .map((date) => ({ label: date, endDate: date })));
    } else {
        for (const row of movementRows) {
            if (row.date < params.startDate) continue;
            if (boundaries.length === 0 || boundaries[boundaries.length - 1].endDate !== row.date) {
                boundaries.push({ label: row.date, endDate: row.date });
            }
        }
    }
    if (boundaries.length === 0) return {};

    const reservedByLabel = await snapshotReservedByBoundary(includedIds, boundaries, params.startDate, params.endDate, params.includeOpening);

    return Object.fromEntries(
        buildBalanceTimeline(seed, movementRows, boundaries)
            .map((point) => [
                point.label,
                Math.round((point.balance - (reservedByLabel.get(point.label) ?? 0)) * 100) / 100,
            ]),
    );
}

/**
 * Reservado (das caixinhas ATIVAS de contas incluídas) ao FIM de cada fronteira, com o
 * MESMO corte de abertura do saldo: com saldo anterior os movimentos pré-período entram;
 * sem, parte de 0. deposit/yield somam, withdraw subtrai (a mesma conta do `currentAmount`).
 * O cursor caminha uma vez pelas fronteiras (já ascendentes) fotografando o acumulado —
 * pra dias futuros (projeção) não há movimento novo, então o reservado fica no atual.
 */
async function snapshotReservedByBoundary(
    includedIds: string[],
    boundaries: BalanceBoundary[],
    startDate: string,
    endDate: string,
    includeOpening: boolean,
): Promise<Map<string, number>> {
    const reservaRows = await db
        .select({
            date: reservaMovements.date,
            type: reservaMovements.type,
            amount: reservaMovements.amount,
        })
        .from(reservaMovements)
        .innerJoin(reservas, eq(reservaMovements.reservaId, reservas.id))
        .where(
            and(
                isNull(reservas.deletedAt),
                eq(reservas.isArchived, 0),
                inArray(reservas.accountId, includedIds),
                inArray(reservaMovements.type, ['deposit', 'withdraw', 'yield']),
                lte(reservaMovements.date, endDate),
                ...(includeOpening ? [] : [gte(reservaMovements.date, startDate)]),
            ),
        )
        .orderBy(asc(reservaMovements.date));

    const reservedByLabel = new Map<string, number>();
    let cursor = 0;
    let index = 0;
    for (const boundary of boundaries) {
        while (index < reservaRows.length && reservaRows[index].date <= boundary.endDate) {
            const row = reservaRows[index];
            cursor += row.type === 'withdraw' ? -row.amount : row.amount;
            index += 1;
        }
        reservedByLabel.set(boundary.label, cursor);
    }
    return reservedByLabel;
}

/**
 * Reservado das caixinhas ATIVAS de contas incluídas no FIM de `date`, reconstruído dos
 * movimentos datados (deposit/yield somam, withdraw subtrai — a mesma conta do extrato, em
 * `snapshotReservedByBoundary`). É o que a manchete da Home desconta num mês PASSADO: com o
 * reservado de hoje, guardar R$ 3.000 em setembro fazia agosto fechar com R$ 3.000 a menos
 * (B4 da análise do e-mail do Denis, 27/09/2026). Mesmo limite do extrato: arquivar ou
 * excluir uma caixinha não é datado.
 */
export async function getReservedAsOf(date: string): Promise<number> {
    const includedIds = (await getIncludedAccounts()).map((account) => account.id);
    if (includedIds.length === 0) return 0;
    const [row] = await db
        .select({
            total: sql<number>`COALESCE(SUM(CASE WHEN ${reservaMovements.type} = 'withdraw' THEN -${reservaMovements.amount} ELSE ${reservaMovements.amount} END), 0)`,
        })
        .from(reservaMovements)
        .innerJoin(reservas, eq(reservaMovements.reservaId, reservas.id))
        .where(
            and(
                isNull(reservas.deletedAt),
                eq(reservas.isArchived, 0),
                inArray(reservas.accountId, includedIds),
                inArray(reservaMovements.type, ['deposit', 'withdraw', 'yield']),
                lte(reservaMovements.date, date),
            ),
        );
    return roundMonetaryValue(row?.total ?? 0);
}

/**
 * Início "aberto" pra expansão de recorrências projetadas: a caminhada sempre parte do
 * cursor da regra (`nextOccurrence`), então uma data-piso anterior a qualquer cursor
 * significa "emite desde o cursor" — é como ocorrência ATRASADA de mês anterior entra
 * na herança da posição. O laço continua limitado pelo guard interno da expansão.
 */
const OPEN_PROJECTION_START = '0000-01-01';

/**
 * Saldo PREVISTO por dia da seção de Pendentes (preferência "Saldo acumulado por dia" +
 * "Pendentes em destaque"): o **Disponível projetado** das contas incluídas ao fim de cada
 * dia que tem pendência — a posição SE tudo até aquele dia se efetivar. Mesma base contábil
 * do saldo confirmado (posição de conta, imune ao modo de exibição), só que o walk inclui,
 * além do confirmado (herança), as PENDÊNCIAS do mês: pendentes avulsas, faturas em aberto
 * (materializadas como despesa pendente na conta de pagamento) e as recorrências PROJETADAS
 * do mês (não-cartão, deduplicadas contra as já lançadas). Recorrência de cartão fica de
 * fora (não move caixa direto — vira fatura). Menos o reservado (as caixinhas não mudam com
 * pendência, então em dia futuro é o reservado atual).
 *
 * Fronteiras = dias COM pendência/projeção no período (é onde a UI mostra o "Previsto"); o
 * confirmado anterior entra só como base da posição, não como fronteira. É espelho direto do
 * getDailyBalanceTimeline, mas pra seção de cima. Independe das linhas visíveis: como o saldo
 * confirmado, é posição — não a soma das linhas de pendente (fatura agrupada, ignorados etc.).
 *
 * `boundaryScope` escolhe QUAIS dias ganham ponto no mapa: 'pending' (padrão) emite só dias
 * com pendência/projeção no período — é a seção de Pendentes; 'movement' emite todo dia com
 * movimento (confirmado, pendente ou projetado) — é a lista inteira com "considerar não
 * efetivadas" ligado, onde cada cabeçalho de dia mostra a posição prevista.
 *
 * Com `includeOpening`, a herança da posição inclui também pendências e recorrências
 * atrasadas ANTERIORES ao período — o previsto do dia 1º é o fechamento projetado do mês
 * anterior, mesma regra do calendário (getCalendarBalanceTimeline).
 */
export async function getProjectedDailyBalanceTimeline(params: {
    startDate: string;
    endDate: string;
    includeOpening: boolean;
    boundaryScope?: 'pending' | 'movement';
}): Promise<Record<string, number>> {
    const includedAccounts = await getIncludedAccounts();
    if (includedAccounts.length === 0) return {};
    const includedIds = includedAccounts.map((account) => account.id);

    const seed = params.includeOpening
        ? buildIncludedAccountBalanceMap(includedAccounts, 'initialBalance')
        : new Map(includedAccounts.map((account) => [account.id, 0]));

    // Confirmado (herança da posição): mesmo recorte do getDailyBalanceTimeline.
    const confirmedRows = await db
        .select({
            date: transactions.date,
            type: transactions.type,
            amount: transactions.amount,
            accountId: transactions.accountId,
            transferDestinationAccountId: transactions.transferDestinationAccountId,
        })
        .from(transactions)
        .where(
            and(
                isNull(transactions.deletedAt),
                eq(transactions.isConfirmed, 1),
                lte(transactions.date, params.endDate),
                sql`${transactions.type} IN ('income', 'expense', 'transfer', 'adjustment')`,
                ...(params.includeOpening ? [] : [gte(transactions.date, params.startDate)]),
            ),
        );

    // Pendentes (avulsas + faturas em aberto): com saldo anterior, as pré-período também
    // entram — só na HERANÇA da posição (fronteira é outra conversa, abaixo). Sem saldo
    // anterior a posição parte de zero no dia 1º, então o recorte volta ao período.
    const pendingRows = await db
        .select({
            date: transactions.date,
            type: transactions.type,
            amount: transactions.amount,
            accountId: transactions.accountId,
            transferDestinationAccountId: transactions.transferDestinationAccountId,
        })
        .from(transactions)
        .where(
            and(
                isNull(transactions.deletedAt),
                eq(transactions.isConfirmed, 0),
                lte(transactions.date, params.endDate),
                sql`${transactions.type} IN ('income', 'expense', 'transfer', 'adjustment')`,
                ...(params.includeOpening ? [] : [gte(transactions.date, params.startDate)]),
            ),
        );

    // Recorrências PROJETADAS do mês (não-cartão), deduplicadas contra as já materializadas
    // pelo slot agendado — espelhando o que a lista de Transações projeta.
    const recurringRows = await db
        .select({
            id: recurringTransactions.id,
            type: recurringTransactions.type,
            amount: recurringTransactions.amount,
            // Saldo previsto: a série ignorada segue movendo dinheiro (ADR 0169) — só carrega.
            isIgnored: recurringTransactions.isIgnored,
            frequency: recurringTransactions.frequency,
            interval: recurringTransactions.interval,
            weekdays: recurringTransactions.weekdays,
            monthlyMode: recurringTransactions.monthlyMode,
            startDate: recurringTransactions.startDate,
            nextOccurrence: recurringTransactions.nextOccurrence,
            endDate: recurringTransactions.endDate,
            skippedDates: recurringTransactions.skippedDates,
            accountId: recurringTransactions.accountId,
            creditCardId: recurringTransactions.creditCardId,
            transferDestinationAccountId: recurringTransactions.transferDestinationAccountId,
        })
        .from(recurringTransactions)
        .where(
            and(
                eq(recurringTransactions.isActive, 1),
                isNull(recurringTransactions.deletedAt),
                isNull(recurringTransactions.creditCardId),
                lte(recurringTransactions.nextOccurrence, params.endDate),
                sql`(${recurringTransactions.endDate} IS NULL OR ${recurringTransactions.endDate} >= ${params.startDate})`,
            ),
        );

    // Com saldo anterior, a projeção emite desde o cursor da regra (ocorrência atrasada de
    // mês anterior entra na herança) — e o dedup precisa enxergar as pré-período já
    // materializadas pra não contar duas vezes.
    const occurrenceSlot = sql<string>`COALESCE(${transactions.recurringOccurrenceDate}, ${transactions.date})`;
    const materializedOccurrences = await db
        .select({ recurringId: transactions.recurringId, occurrenceDate: occurrenceSlot })
        .from(transactions)
        .where(
            and(
                isNull(transactions.deletedAt),
                lte(occurrenceSlot, params.endDate),
                sql`${transactions.recurringId} IS NOT NULL`,
                ...(params.includeOpening ? [] : [gte(occurrenceSlot, params.startDate)]),
            ),
        );

    const projectedRows = buildProjectedRecurringTransactions(
        recurringRows,
        params.includeOpening ? OPEN_PROJECTION_START : params.startDate,
        params.endDate,
        new Set(
            materializedOccurrences
                .filter((row) => row.recurringId)
                .map((row) => `${row.recurringId}:${row.occurrenceDate}`),
        ),
    ) as DatedBalanceImpactRow[];

    // Fronteiras: onde o "Previsto" aparece. 'pending' = dias com pendência/projeção no
    // período (seção de Pendentes); 'movement' soma os dias de movimento confirmado — a
    // lista inteira quer posição prevista em todo cabeçalho de dia. Impacto pré-período
    // participa da herança mas nunca vira fronteira.
    const boundaryDates = new Set<string>();
    for (const row of pendingRows) {
        if (row.date >= params.startDate) boundaryDates.add(row.date);
    }
    for (const row of projectedRows) {
        if (row.date >= params.startDate) boundaryDates.add(row.date);
    }
    if (params.boundaryScope === 'movement') {
        for (const row of confirmedRows) {
            if (row.date >= params.startDate) boundaryDates.add(row.date);
        }
    }
    if (boundaryDates.size === 0) return {};
    const boundaries: BalanceBoundary[] = [...boundaryDates]
        .sort((left, right) => compareIso(left, right))
        .map((date) => ({ label: date, endDate: date }));

    // Walk único sobre confirmado + pendentes + projetadas, ordenado por data.
    const impactRows: DatedBalanceImpactRow[] = [...confirmedRows, ...pendingRows, ...projectedRows]
        .sort((left, right) => compareIso(left.date, right.date));

    const reservedByLabel = await snapshotReservedByBoundary(includedIds, boundaries, params.startDate, params.endDate, params.includeOpening);

    return Object.fromEntries(
        buildBalanceTimeline(seed, impactRows, boundaries)
            .map((point) => [
                point.label,
                Math.round((point.balance - (reservedByLabel.get(point.label) ?? 0)) * 100) / 100,
            ]),
    );
}

/** Um dia do calendário de fluxo de caixa. */
export interface CalendarBalanceDay {
    /** YYYY-MM-DD. */
    date: string;
    /** Disponível ao FIM do dia. Mesma base contábil do saldo acumulado do extrato. */
    balance: number;
    /**
     * Houve movimento NESTE dia. Dia sem movimento carrega o saldo do dia anterior —
     * o número é real, mas não houve nada acontecendo. A UI usa isso pra diferenciar
     * "dia parado" de "dia que mexeu no caixa" sem inventar cor pro saldo herdado.
     */
    hasMovement: boolean;
}

/**
 * Saldo do Disponível para TODO dia do período — a série que o calendário de fluxo de
 * caixa desenha. Difere do getDailyBalanceTimeline/getProjectedDailyBalanceTimeline num
 * ponto só: aqueles emitem apenas dias COM movimento (é o que o extrato quer, saldo só
 * onde houve dinheiro), e um calendário precisa de número em toda célula. Aqui toda data
 * do intervalo é fronteira, então o dia parado herda naturalmente o saldo do anterior —
 * sem "forward-fill" à parte, é o mesmo walk com fronteiras mais densas.
 *
 * Sempre com saldo anterior (o equivalente a includeOpening: true), sem preferência: a
 * pergunta do calendário é "quanto eu TENHO no dia 20", que é posição. Partir de zero no
 * dia 1º pintaria de vermelho um mês inteiro que está positivo.
 *
 * `includePending` é o "considerar despesas e receitas não efetivadas" da tela: false =
 * só o confirmado; true = soma pendentes, faturas de cartão em aberto (que já viram
 * despesa pendente na conta de pagamento pelo ensureInvoiceExpenseTransaction) e as
 * recorrências projetadas não-cartão, deduplicadas contra as já materializadas — mesmo
 * conjunto do getProjectedDailyBalanceTimeline. Pendência ANTERIOR ao período também
 * entra (na herança): o dia 1º parte do fechamento projetado do mês anterior, e a grade
 * de agosto emenda exatamente onde a de julho parou.
 *
 * Herda as mesmas regras e o mesmo limite do reservado das caixinhas documentados no
 * getDailyBalanceTimeline (inclusive o Disponível poder ficar negativo).
 */
export async function getCalendarBalanceTimeline(params: {
    startDate: string;
    endDate: string;
    includePending: boolean;
}): Promise<CalendarBalanceDay[]> {
    const includedAccounts = await getIncludedAccounts();
    if (includedAccounts.length === 0) return [];
    const includedIds = includedAccounts.map((account) => account.id);

    const seed = buildIncludedAccountBalanceMap(includedAccounts, 'initialBalance');

    // Confirmado: tudo até o fim do período, inclusive pré-período (é o que forma a
    // posição herdada do dia 1º).
    const confirmedRows = await db
        .select({
            date: transactions.date,
            type: transactions.type,
            amount: transactions.amount,
            accountId: transactions.accountId,
            transferDestinationAccountId: transactions.transferDestinationAccountId,
        })
        .from(transactions)
        .where(
            and(
                isNull(transactions.deletedAt),
                eq(transactions.isConfirmed, 1),
                lte(transactions.date, params.endDate),
                sql`${transactions.type} IN ('income', 'expense', 'transfer', 'adjustment')`,
            ),
        );

    let impactRows: DatedBalanceImpactRow[] = confirmedRows;

    if (params.includePending) {
        // Pendente TAMBÉM sem recorte de início: com "considerar não efetivadas" ligado, a
        // posição herdada do dia 1º é o fechamento PROJETADO do mês anterior — uma pendência
        // de julho ainda em aberto precisa entrar no saldo que agosto herda, senão a virada
        // de mês "esquece" tudo que não efetivou (bug reportado pelo Jeferson, 29/jul).
        const pendingRows = await db
            .select({
                date: transactions.date,
                type: transactions.type,
                amount: transactions.amount,
                accountId: transactions.accountId,
                transferDestinationAccountId: transactions.transferDestinationAccountId,
            })
            .from(transactions)
            .where(
                and(
                    isNull(transactions.deletedAt),
                    eq(transactions.isConfirmed, 0),
                    lte(transactions.date, params.endDate),
                    sql`${transactions.type} IN ('income', 'expense', 'transfer', 'adjustment')`,
                ),
            );

        const recurringRows = await db
            .select({
                id: recurringTransactions.id,
                type: recurringTransactions.type,
                amount: recurringTransactions.amount,
                isIgnored: recurringTransactions.isIgnored,
                frequency: recurringTransactions.frequency,
                interval: recurringTransactions.interval,
                weekdays: recurringTransactions.weekdays,
                monthlyMode: recurringTransactions.monthlyMode,
                startDate: recurringTransactions.startDate,
                nextOccurrence: recurringTransactions.nextOccurrence,
                endDate: recurringTransactions.endDate,
                skippedDates: recurringTransactions.skippedDates,
                accountId: recurringTransactions.accountId,
                creditCardId: recurringTransactions.creditCardId,
                transferDestinationAccountId: recurringTransactions.transferDestinationAccountId,
            })
            .from(recurringTransactions)
            .where(
                and(
                    eq(recurringTransactions.isActive, 1),
                    isNull(recurringTransactions.deletedAt),
                    isNull(recurringTransactions.creditCardId),
                    lte(recurringTransactions.nextOccurrence, params.endDate),
                    sql`(${recurringTransactions.endDate} IS NULL OR ${recurringTransactions.endDate} >= ${params.startDate})`,
                ),
            );

        // Dedup sem recorte de início pelo mesmo motivo: a projeção agora emite desde o
        // cursor da regra (ocorrência ATRASADA de mês anterior entra na herança), então uma
        // ocorrência pré-período já materializada precisa estar no conjunto pra não contar duas vezes.
        const occurrenceSlot = sql<string>`COALESCE(${transactions.recurringOccurrenceDate}, ${transactions.date})`;
        const materializedOccurrences = await db
            .select({ recurringId: transactions.recurringId, occurrenceDate: occurrenceSlot })
            .from(transactions)
            .where(
                and(
                    isNull(transactions.deletedAt),
                    lte(occurrenceSlot, params.endDate),
                    sql`${transactions.recurringId} IS NOT NULL`,
                ),
            );

        const projectedRows = buildProjectedRecurringTransactions(
            recurringRows,
            OPEN_PROJECTION_START,
            params.endDate,
            new Set(
                materializedOccurrences
                    .filter((row) => row.recurringId)
                    .map((row) => `${row.recurringId}:${row.occurrenceDate}`),
            ),
        ) as DatedBalanceImpactRow[];

        impactRows = [...confirmedRows, ...pendingRows, ...projectedRows];
    }

    impactRows = [...impactRows].sort((left, right) => compareIso(left.date, right.date));

    // Toda data do intervalo vira fronteira — é o que dá número em dia parado.
    const boundaries: BalanceBoundary[] = eachDateBetween(params.startDate, params.endDate)
        .map((date) => ({ label: date, endDate: date }));
    if (boundaries.length === 0) return [];

    const movementDates = new Set(
        impactRows
            .filter((row) => row.date >= params.startDate && row.date <= params.endDate)
            .map((row) => row.date),
    );

    const reservedByLabel = await snapshotReservedByBoundary(
        includedIds,
        boundaries,
        params.startDate,
        params.endDate,
        true,
    );

    return buildBalanceTimeline(seed, impactRows, boundaries).map((point) => ({
        date: point.label,
        balance: Math.round((point.balance - (reservedByLabel.get(point.label) ?? 0)) * 100) / 100,
        hasMovement: movementDates.has(point.label),
    }));
}

/** Datas YYYY-MM-DD de `start` a `end`, inclusive. Vazio se o intervalo for invertido. */
function eachDateBetween(start: string, end: string): string[] {
    if (start > end) return [];
    const dates: string[] = [];
    const cursor = parseISO(start);
    const last = parseISO(end);
    for (let day = cursor; day <= last; day = addDays(day, 1)) {
        dates.push(format(day, 'yyyy-MM-dd'));
    }
    return dates;
}

export async function reorderAccounts(orderedIds: string[]): Promise<void> {
    const now = new Date().toISOString();
    for (let i = 0; i < orderedIds.length; i++) {
        await db
            .update(accounts)
            .set({ sortOrder: i, updatedAt: now })
            .where(eq(accounts.id, orderedIds[i]));
    }
}
